import { ArrowRight, Calendar, Clock } from 'lucide-react';
import { useTranslation } from '../utils/useTranslation';

interface LatestPostsProps {
    onNavigate: (view: any) => void;
}

export function LatestPosts({ onNavigate }: LatestPostsProps) {
    const { t } = useTranslation();

    const posts = [
        {
            id: 'blog-post-6',
            title: t('blog.posts.post6.title'),
            excerpt: t('blog.posts.post6.excerpt'),
            date: '2025-10-28',
            readTime: '14 min',
            image: 'https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b?auto=format&fit=crop&q=80',
            category: t('blog.categories.trends'),
            gradient: 'from-blue-600 to-teal-600'
        },
        {
            id: 'blog-post-5',
            title: t('blog.posts.post5.title'),
            excerpt: t('blog.posts.post5.excerpt'),
            date: '2025-11-01',
            readTime: '12 min',
            image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&q=80',
            category: t('blog.categories.technology'),
            gradient: 'from-indigo-600 to-cyan-600'
        },
        {
            id: 'blog-post-4',
            title: t('blog.posts.post4.title'),
            excerpt: t('blog.posts.post4.excerpt'),
            date: '2025-11-05',
            readTime: '18 min',
            image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&q=80',
            category: t('blog.categories.tips'),
            gradient: 'from-purple-600 to-pink-600'
        }
    ];

    return (
        <div className="py-20 bg-white">
            <div className="container mx-auto px-4 max-w-6xl">
                <div className="text-center mb-12">
                    <h2 className="text-4xl font-bold text-gray-800 mb-4">
                        {t('blog.latest.title')}
                    </h2>
                    <p className="text-xl text-gray-600">
                        {t('blog.latest.subtitle')}
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                    {posts.map((post, index) => (
                        <div
                            key={index}
                            className="group bg-white rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col h-full cursor-pointer"
                            onClick={() => onNavigate(post.id)}
                        >
                            {/* Image with Gradient Overlay */}
                            <div className="h-48 w-full relative overflow-hidden">
                                <div
                                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                                    style={{ backgroundImage: `url('${post.image}')` }}
                                ></div>
                                <div className={`absolute inset-0 bg-gradient-to-br ${post.gradient} opacity-60 group-hover:opacity-40 transition-opacity duration-300`}></div>

                                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-gray-700 uppercase tracking-wide shadow-sm">
                                    {post.category}
                                </div>
                            </div>

                            <div className="p-6 flex flex-col flex-1">
                                <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                                    <div className="flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        {post.date}
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        {post.readTime}
                                    </div>
                                </div>

                                <h3 className="text-xl font-bold text-gray-800 mb-3 line-clamp-2 group-hover:text-purple-600 transition-colors">
                                    {post.title}
                                </h3>

                                <p className="text-gray-600 text-sm mb-6 line-clamp-3 flex-1">
                                    {post.excerpt}
                                </p>

                                <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                                    {t('blog.latest.readMore')}
                                    <ArrowRight className="w-4 h-4 ml-1" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
